import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Breadcrumbs,
  Alert,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Payment as PaymentIcon,
  Category as CategoryIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
  Share as ShareIcon,
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  YouTube as YouTubeIcon,
  LinkedIn as LinkedInIcon,
  MusicNote as TikTokIcon
} from '@mui/icons-material';

// Import custom components
import { Card } from '../../../design-system/components/core';
import { Button } from '../../../design-system/components/core';
import MagicLoading from '../../../components/ui/MagicLoading';
import TabPanel from '../../../components/ui/TabPanel';
import EntityStatusBadge from '../../../components/core/EntityStatusBadge';
import DetailPageLayout from '../../../components/layout/DetailPageLayout';

// Import hooks and stores
import useVendorStore from '../../../stores/vendorStore';
import useAuthStore from '../../../stores/authStore';
import useUIStore from '../../../stores/uiStore';
import useConfirmationDialog from '../../../hooks/useConfirmationDialog';

// Import utilities
import { formatCurrencyWithSymbol } from '../../../utils/currencyUtils';
import { VENDOR_TYPES, PAYMENT_METHODS, SOCIAL_PLATFORMS } from '../../../constants/vendorConstants';

const VendorDetail = () => {
  const { id } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Store hooks
  const { currentVendor, isLoading, error, fetchVendorById, deleteVendor } = useVendorStore();
  const { hasPermission } = useAuthStore();
  const { addNotification } = useUIStore();
  
  // State
  const [currentTab, setCurrentTab] = useState(0);
  
  // Confirmation dialog
  const [ConfirmDialog, confirm] = useConfirmationDialog();
  
  // Check permissions
  const canUpdate = hasPermission('vendors', 'update');
  const canDelete = hasPermission('vendors', 'delete');
  
  // Fetch vendor data
  useEffect(() => {
    if (id) {
      fetchVendorById(id);
    }
  }, [id, fetchVendorById]);
  
  // Handle vendor deletion
  const handleDelete = async () => {
    const confirmed = await confirm({
      title: `Delete ${currentVendor.name}?`,
      content: 'This will permanently delete the vendor. This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDelete: true
    });
    
    if (confirmed) {
      try {
        await deleteVendor(id);
        addNotification({
          message: 'Vendor deleted successfully',
          type: 'success'
        });
        navigate('/finances/vendors');
      } catch (error) {
        addNotification({
          message: error.message || 'Failed to delete vendor',
          type: 'error'
        });
      }
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };
  
  const getVendorTypeLabel = (type) => {
    const typeOption = VENDOR_TYPES.find(t => t.value === type);
    return typeOption ? typeOption.label : type.replace('_', ' ').toUpperCase();
  };
  
  const getPaymentMethodLabel = (method) => {
    const methodOption = PAYMENT_METHODS.find(m => m.value === method);
    return methodOption ? methodOption.label : method.replace('_', ' ').toUpperCase();
  };
  
  if (isLoading || !currentVendor) {
    return <MagicLoading />;
  }
  
  return (
    <DetailPageLayout
      title={currentVendor.name}
      subtitle={`${getVendorTypeLabel(currentVendor.type)} ${currentVendor.code ? `· ${currentVendor.code}` : ''}`}
      breadcrumbs={[
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Finances', path: '/finances' },
        { label: 'Vendors', path: '/finances/vendors' },
        { label: currentVendor.name }
      ]}
      actions={[
        ...(canUpdate ? [{
          label: 'Edit',
          onClick: () => navigate(`/finances/vendors/${id}/edit`),
          icon: <EditIcon />,
          variant: 'outlined'
        }] : []),
        ...(canDelete ? [{
          label: 'Delete',
          onClick: handleDelete,
          icon: <DeleteIcon />,
          variant: 'outlined',
          color: 'error'
        }] : [])
      ]}
      stats={[
        {
          label: 'Total Amount',
          value: formatCurrencyWithSymbol(currentVendor.totalAmount || 0),
          icon: <MoneyIcon />,
          color: 'primary'
        },
        {
          label: 'Total Expenses',
          value: currentVendor.totalExpenses || 0,
          icon: <PaymentIcon />,
          color: 'secondary'
        },
        {
          label: 'Categories',
          value: currentVendor.categories?.length || 0,
          icon: <CategoryIcon />,
          color: 'info'
        },
        {
          label: 'Rating',
          value: currentVendor.rating || 'N/A',
          icon: <DescriptionIcon />,
          color: 'warning'
        }
      ]}
    >
      {/* Status Badge */}
      {currentVendor.status !== 'active' && (
        <Alert severity={currentVendor.status === 'suspended' ? 'warning' : 'error'} sx={{ mb: 3 }}>
          This vendor is currently {currentVendor.status}
        </Alert>
      )}
      
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="Details" icon={<BusinessIcon />} iconPosition="start" />
          <Tab label="Payment" icon={<PaymentIcon />} iconPosition="start" />
          <Tab label="Categories" icon={<CategoryIcon />} iconPosition="start" />
          {currentVendor.type === 'influencer' && (
            <Tab label="Social Media" icon={<ShareIcon />} iconPosition="start" />
          )}
        </Tabs>
      </Box>
      
      {/* Details Tab */}
      <TabPanel value={currentTab} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Name
                    </Typography>
                    <Typography variant="body1">{currentVendor.name}</Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Code
                    </Typography>
                    <Typography variant="body1">{currentVendor.code || 'N/A'}</Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Type
                    </Typography>
                    <Chip
                      label={getVendorTypeLabel(currentVendor.type)}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Status
                    </Typography>
                    <EntityStatusBadge
                      status={currentVendor.status}
                      entityType="vendor"
                      size="small"
                    />
                  </Box>
                  
                  {currentVendor.description && (
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Description
                      </Typography>
                      <Typography variant="body1">{currentVendor.description}</Typography>
                    </Box>
                  )}
                  
                  {currentVendor.notes && (
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Notes
                      </Typography>
                      <Typography variant="body1">{currentVendor.notes}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Contact Information
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {currentVendor.contact?.email && (
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Email
                      </Typography>
                      <Typography variant="body1">{currentVendor.contact.email}</Typography>
                    </Box>
                  )}
                  
                  {currentVendor.contact?.phone && (
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Phone
                      </Typography>
                      <Typography variant="body1">{currentVendor.contact.phone}</Typography>
                    </Box>
                  )}
                  
                  {currentVendor.contact?.website && (
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Website
                      </Typography>
                      <Typography variant="body1">{currentVendor.contact.website}</Typography>
                    </Box>
                  )}
                  
                  {currentVendor.fullAddress && (
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Address
                      </Typography>
                      <Typography variant="body1">{currentVendor.fullAddress}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
      
      {/* Payment Tab */}
      <TabPanel value={currentTab} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Payment Information
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Preferred Method
                    </Typography>
                    <Chip
                      label={getPaymentMethodLabel(currentVendor.paymentInfo?.preferredMethod || 'bank_transfer')}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Payment Terms
                    </Typography>
                    <Typography variant="body1">
                      {currentVendor.paymentInfo?.paymentTermsDays || 30} days
                    </Typography>
                  </Box>
                  
                  {currentVendor.currency && (
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Currency
                      </Typography>
                      <Typography variant="body1">{currentVendor.currency}</Typography>
                    </Box>
                  )}
                  
                  {currentVendor.taxId && (
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Tax ID
                      </Typography>
                      <Typography variant="body1">{currentVendor.taxId}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            {currentVendor.paymentInfo?.preferredMethod === 'bank_transfer' && 
             currentVendor.paymentInfo?.bankDetails && (
              <Card>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Bank Details
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {Object.entries(currentVendor.paymentInfo.bankDetails).map(([key, value]) => (
                      value && (
                        <Box key={key}>
                          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </Typography>
                          <Typography variant="body1">{value}</Typography>
                        </Box>
                      )
                    ))}
                  </Box>
                </Box>
              </Card>
            )}
            
            {['instapay', 'paypal', 'cryptocurrency'].includes(currentVendor.paymentInfo?.preferredMethod) && 
             currentVendor.paymentInfo?.digitalPayment && (
              <Card>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Digital Payment Details
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {Object.entries(currentVendor.paymentInfo.digitalPayment).map(([key, value]) => (
                      value && (
                        <Box key={key}>
                          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </Typography>
                          <Typography variant="body1">{value}</Typography>
                        </Box>
                      )
                    ))}
                  </Box>
                </Box>
              </Card>
            )}
          </Grid>
        </Grid>
      </TabPanel>
      
      {/* Categories Tab */}
      <TabPanel value={currentTab} index={2}>
        <Card>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Categories & Specializations
            </Typography>
            
            {currentVendor.categories && currentVendor.categories.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Categories
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {currentVendor.categories.map((category, index) => (
                    <Chip
                      key={index}
                      label={category.replace('_', ' ').toUpperCase()}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
            
            {currentVendor.specializations && currentVendor.specializations.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Specializations
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {currentVendor.specializations.map((spec, index) => (
                    <Chip
                      key={index}
                      label={spec}
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
            
            {currentVendor.tags && currentVendor.tags.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Tags
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {currentVendor.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Card>
      </TabPanel>
      
      {/* Social Media Tab (for influencers) */}
      {currentVendor.type === 'influencer' && (
        <TabPanel value={currentTab} index={3}>
          <Grid container spacing={3}>
            {SOCIAL_PLATFORMS.map((platform) => {
              const data = currentVendor.socialMedia?.[platform.value];
              // Show platform if there's any data (handle, followers, etc.)
              const hasData = data && (data.handle || data.followers || data.subscribers || 
                                      data.engagementRate || data.avgViews || data.connections);
              if (!hasData) return null;
              
              const getPlatformIcon = () => {
                switch(platform.value) {
                  case 'instagram': return <InstagramIcon />;
                  case 'tiktok': return <TikTokIcon />;
                  case 'facebook': return <FacebookIcon />;
                  case 'twitter': return <TwitterIcon />;
                  case 'youtube': return <YouTubeIcon />;
                  case 'linkedin': return <LinkedInIcon />;
                  default: return <ShareIcon />;
                }
              };
              
              return (
                <Grid item xs={12} md={6} key={platform.value}>
                  <Card>
                    <Box sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        {getPlatformIcon()}
                        <Typography variant="h6">{platform.label}</Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {data.handle && (
                          <Box>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                              Handle
                            </Typography>
                            <Typography variant="body1">@{data.handle}</Typography>
                          </Box>
                        )}
                        
                        {(data.followers || data.subscribers) && (
                          <Box>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                              {platform.value === 'youtube' ? 'Subscribers' : 'Followers'}
                            </Typography>
                            <Typography variant="body1">
                              {(data.followers || data.subscribers).toLocaleString()}
                            </Typography>
                          </Box>
                        )}
                        
                        {data.engagementRate && (
                          <Box>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                              Engagement Rate
                            </Typography>
                            <Typography variant="body1">{data.engagementRate}%</Typography>
                          </Box>
                        )}
                        
                        {data.avgViews && (
                          <Box>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                              Average Views
                            </Typography>
                            <Typography variant="body1">{data.avgViews.toLocaleString()}</Typography>
                          </Box>
                        )}
                        
                        {data.connections && (
                          <Box>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                              Connections
                            </Typography>
                            <Typography variant="body1">{data.connections.toLocaleString()}</Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </TabPanel>
      )}
      
      <ConfirmDialog />
    </DetailPageLayout>
  );
};

export default VendorDetail;